import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Dropdown } from 'antd';
import {
  BulbOutlined,
  SunOutlined,
  MoonOutlined,
  DesktopOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useModel } from '@umijs/max';

export type ThemeType = 'light' | 'realDark' | 'system';

interface ThemeDropdownProps {
  onThemeChange?: (value: ThemeType) => void;
}

const ThemeDropdown: React.FC<ThemeDropdownProps> = ({ onThemeChange }) => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const latestSetInitialState = useRef(setInitialState);
  const latestOnThemeChange = useRef(onThemeChange);

  // 保持最新的回调引用
  useEffect(() => {
    latestSetInitialState.current = setInitialState;
    latestOnThemeChange.current = onThemeChange;
  });

  const [theme, setTheme] = useState<ThemeType>(() => {
    // 首先尝试从localStorage获取用户明确选择的主题设置
    const savedTheme = localStorage.getItem('app-theme') as ThemeType;
    if (savedTheme && ['light', 'realDark', 'system'].includes(savedTheme)) {
      return savedTheme;
    }

    // 其次从全局设置中获取主题
    if (
      initialState?.settings?.navTheme &&
      ['light', 'realDark', 'system'].includes(initialState.settings.navTheme)
    ) {
      return initialState.settings.navTheme as ThemeType;
    }

    // 默认使用系统主题
    return 'system';
  });

  // 使用useCallback优化函数性能
  const applySystemTheme = useCallback(() => {
    try {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const themeToApply = prefersDark ? 'realDark' : 'light';

      // 应用主题到DOM
      document.documentElement.setAttribute('data-theme', themeToApply);

      // 更新body类名以保持一致性
      if (prefersDark) {
        document.body.className = document.body.className.replace(/\blight\b/, 'dark');
      } else {
        document.body.className = document.body.className.replace(/\bdark\b/, 'light');
      }

      // 这里关键是不要更改navTheme为实际应用的主题，而是保持为'system'
      if (latestSetInitialState.current && theme === 'system') {
        latestSetInitialState.current((prev) => ({
          ...prev,
          settings: {
            ...prev?.settings,
            navTheme: 'light' as const, // 使用 light 作为默认值
            _currentTheme: themeToApply, // 临时属性记录当前显示的主题
          },
        }));
      }

      if (latestOnThemeChange.current && theme === 'system')
        latestOnThemeChange.current(themeToApply as ThemeType);
      return themeToApply;
    } catch {
      return 'light'; // 默认回退到亮色主题
    }
  }, [theme]);

  // 应用主题的通用方法
  const applyTheme = useCallback(
    (themeToApply: ThemeType) => {
      try {
        // 1. 保存原始的主题选择到localStorage
        localStorage.setItem('app-theme', themeToApply);

        // 2. 更新全局状态中的navTheme为用户选择的原始主题
        if (latestSetInitialState.current) {
          latestSetInitialState.current((prev) => ({
            ...prev,
            settings: {
              ...(prev?.settings || {}),
              navTheme: (themeToApply === 'system' ? 'light' : themeToApply) as
                | 'light'
                | 'realDark', // 确保类型正确
            },
          }));

          // 3. 同时更新theme-settings到localStorage以保持兼容性
          const newSettings = {
            ...(initialState?.settings || {}),
            navTheme: themeToApply, // 保存用户的原始主题选择
          };
          localStorage.setItem('theme-settings', JSON.stringify(newSettings));
        }

        // 4. 应用实际的视觉主题
        if (themeToApply === 'system') {
          applySystemTheme();
        } else {
          document.documentElement.setAttribute('data-theme', themeToApply);

          // 更新body类名
          if (themeToApply === 'realDark') {
            document.body.className = document.body.className.replace(/\blight\b/, 'dark');
          } else {
            document.body.className = document.body.className.replace(/\bdark\b/, 'light');
          }

          if (latestOnThemeChange.current) latestOnThemeChange.current(themeToApply);
        }
      } catch {
        // 忽略错误，保持静默
      }
    },
    [applySystemTheme, initialState],
  );

  // 主题变化时应用 - 只在theme状态改变时触发
  useEffect(() => {
    applyTheme(theme);
  }, [theme]); // 移除applyTheme依赖，防止无限循环

  // 监听系统主题变化
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (theme === 'system') {
        applySystemTheme();
      }
    };

    // 初始化时立即检查系统主题
    if (theme === 'system') {
      handleChange();
    }

    // 添加事件监听
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, applySystemTheme]);

  // 在组件挂载时，立即应用当前主题 - 只执行一次
  useEffect(() => {
    const applyInitialTheme = () => {
      applyTheme(theme);
    };

    // 确保DOM加载完毕
    if (document.readyState === 'complete') {
      applyInitialTheme();
    } else {
      const handleLoad = () => applyInitialTheme();
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // 移除调试日志以避免eslint警告
  // 可以在开发时临时启用以下代码进行调试：
  // useEffect(() => {
  //   console.log('当前主题设置:', {
  //     theme,
  //     savedTheme: localStorage.getItem('app-theme'),
  //     initialStateTheme: initialState?.settings?.navTheme,
  //   });
  // }, [theme, initialState]);

  const items: MenuProps['items'] = [
    {
      key: 'light',
      icon: <SunOutlined />,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          亮色模式
          {theme === 'light' && <CheckOutlined style={{ marginLeft: 8 }} />}
        </div>
      ),
      onClick: () => setTheme('light'),
    },
    {
      key: 'dark',
      icon: <MoonOutlined />,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          暗色模式
          {theme === 'realDark' && <CheckOutlined style={{ marginLeft: 8 }} />}
        </div>
      ),
      onClick: () => setTheme('realDark'),
    },
    {
      key: 'system',
      icon: <DesktopOutlined />,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          跟随系统
          {theme === 'system' && <CheckOutlined style={{ marginLeft: 8 }} />}
        </div>
      ),
      onClick: () => setTheme('system'),
    },
  ];

  return (
    <Dropdown menu={{ items, selectedKeys: [theme] }} placement="bottomRight">
      <div
        style={{
          display: 'flex',
          height: 26,
          cursor: 'pointer',
          alignItems: 'center',
        }}
      >
        <BulbOutlined />
      </div>
    </Dropdown>
  );
};

export default ThemeDropdown;
